import { useEffect, useRef } from 'react';
import { Article, getLocalNews } from '../News/helpers';
import { useComponentRef } from './useComponentRef';
import { Device } from '../../types';
import { getWeather } from '../Weather/helpers';

const WEATHER_FETCH_INTERVAL = 30 * 60 * 1000;
const NEWS_FETCH_INTERVAL = 30 * 60 * 1000;
const MAX_RETRY_COUNT = 5;
const RETRY_DELAY_MS = 2000;

type UseWeatherNewsComponentsProps = {
  device: Device | undefined,
};

const delay = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

export const useWeatherComponent = ({ device }: UseWeatherNewsComponentsProps): void => {
  const componentRef = useComponentRef();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!device) return undefined;

    let destroyed = false;
    let retryCount = 0;

    const weatherError = () => {
      // @ts-ignore
      componentRef.current.weather = { error: true };
      retryCount = 0;
    };

    const fetchWeather = async (): Promise<void> => {
      if (destroyed) return;

      const lat = (device as Device)?.latitude;
      const lng = (device as Device)?.longitude;

      if (!lat || !lng) {
        weatherError();
        return;
      }

      const response = await getWeather({ lat, lng });

      if (destroyed) return;

      if (response.error && retryCount < MAX_RETRY_COUNT) {
        retryCount += 1;
        await delay(RETRY_DELAY_MS);
        await fetchWeather();
        return;
      }

      if (retryCount >= MAX_RETRY_COUNT) {
        weatherError();
        return;
      }

      retryCount = 0;
      // @ts-ignore
      componentRef.current.weather = {
        ...response,
        duration: 15,
        error: false,
      };
    };

    fetchWeather();

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchWeather, WEATHER_FETCH_INTERVAL);

    return () => {
      destroyed = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [device]); // eslint-disable-line react-hooks/exhaustive-deps
};

const useNewsComponent = ({ device }: UseWeatherNewsComponentsProps) => {
  const componentRef = useComponentRef();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!device) return undefined;

    let destroyed = false;
    let retryCount = 0;

    const newsError = () => {
      // @ts-ignore
      componentRef.current.news = { error: true };
      retryCount = 0;
    };

    const fetchNews = async (): Promise<void> => {
      if (destroyed) return;

      const response = await getLocalNews();

      if (destroyed) return;

      if ((response.error || !response.articles) && retryCount < MAX_RETRY_COUNT) {
        retryCount += 1;
        await delay(RETRY_DELAY_MS);
        await fetchNews();
        return;
      }

      if (retryCount >= MAX_RETRY_COUNT) {
        newsError();
        return;
      }

      const { articles } = response;

      if (!articles) {
        newsError();
        return;
      }

      const filteredArticles = articles.filter(
        (article: Article) => article.urlToImage && article.url && article.title && article.publishedAt,
      );

      if (filteredArticles.length === 0) {
        newsError();
        return;
      }

      const index = Math.floor(Math.random() * filteredArticles.length);
      let article = filteredArticles[index];

      if (article.title.includes(' - ')) {
        article = {
          ...article,
          title: article.title.substring(0, article.title.indexOf(' - ')),
        };
      }

      retryCount = 0;
      componentRef.current.news = {
        article, duration: 15, weight: 1, error: false,
      };
    };

    fetchNews();

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchNews, NEWS_FETCH_INTERVAL);

    return () => {
      destroyed = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [device]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const useWeatherNewsComponents = (
  { device }: UseWeatherNewsComponentsProps,
): void => {
  useNewsComponent({ device });
  useWeatherComponent({ device });
};
